import { FormattedMessage, useIntl } from 'react-intl';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Button from '@alltrails/denali/components/Button';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import { useExperiments } from '@alltrails/experiments';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import BaseInstallInterstitial from '../BaseInstallInterstitial';
import useDeeplink from '../../hooks/useDeeplink';
import { deepLinkRedirect } from '../../utils';
import AppPromotion from '../AppPromotion';
import styles from './InstallVideoVariantInterstitial.module.scss';
import posterGeneric from './poster_generic.avif';
import posterEn from './poster_en.avif';

type InstallVideoVariantInterstitialProps = {
  isOpen?: boolean;
  onRequestClose?: () => void;
  promptLocation: InstallPromptLocation;
  displayTrigger?: CarouselDisplayTrigger;
  deeplinkData?: DeeplinkData;
};

const InstallVideoVariantInterstitial = ({
  isOpen = false,
  onRequestClose,
  promptLocation,
  displayTrigger,
  deeplinkData
}: InstallVideoVariantInterstitialProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const intl = useIntl();
  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';
  const getDeeplink = useDeeplink({ deeplinkData, isOpen, promptType: 'interstitial', displayTrigger });

  const baseUrl = 'https://cdn-assets.alltrails.com/assets/videos';
  const videoSrc = languageRegionCode === 'en-US' ? `${baseUrl}/navigate_en_US.mp4` : `${baseUrl}/navigate_generic_all.mp4`;

  const handleContinueInApp = () => {
    deepLinkRedirect(getDeeplink(), 'interstitial', promptLocation, isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native', displayTrigger);
  };

  return (
    <BaseInstallInterstitial
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      promptLocation={promptLocation}
      displayTrigger={displayTrigger}
      isDeviceHeight
      hidePageScrollbarGutter
    >
      <div className={styles.content}>
        <AppPromotion heading={<FormattedMessage defaultMessage="AllTrails is better in the app" />}>
          <div className={styles.videoContainer}>
            <video
              poster={languageRegionCode === 'en-US' ? posterEn.src : posterGeneric.src}
              className={styles.video}
              // TODO add translated title: https://alltrails.atlassian.net/browse/GROW-12487
              title=""
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
            <div className={styles.gradient} />
          </div>
        </AppPromotion>
        <div className={styles.buttonContainer}>
          <Button
            width="fullOnMobile"
            variant="primary"
            text={intl.formatMessage({ defaultMessage: 'Continue in app' })}
            onClick={handleContinueInApp}
            testId="continue-in-app-button"
          />
        </div>
      </div>
    </BaseInstallInterstitial>
  );
};

export default InstallVideoVariantInterstitial;
